import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../store/hooks';
import { registerUser, clearError } from '../../store/slices/authSlice';
import { useAuth } from '../../hooks/useAuth';
import { Input } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Modal';
import { config } from '../../config/env';
import LogoImage from '../../assets/logo.png';
import toast from 'react-hot-toast';
import { Ico } from '../../components/ui/Icon';

const schema = z.object({
  first_name:            z.string().min(2, 'At least 2 characters'),
  last_name:             z.string().min(2, 'At least 2 characters'),
  email:                 z.string().email('Invalid email'),
  username:              z.string().min(3, 'At least 3 characters').regex(/^[a-zA-Z0-9_]+$/, 'Letters, numbers, underscores only'),
  password:              z.string().min(8, 'At least 8 characters'),
  password_confirmation: z.string(),
}).refine(d => d.password === d.password_confirmation, {
  message: "Passwords don't match", path: ['password_confirmation'],
});
type FormData = z.infer<typeof schema>;

const Register: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error, isAuthenticated } = useAuth();
  const [logoFailed, setLogoFailed] = React.useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  // FIX: same guard as Login — only navigate when truly authenticated with no error
  useEffect(() => {
    if (isAuthenticated && !error) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, error, navigate]);

  useEffect(() => { return () => { dispatch(clearError()); }; }, [dispatch]);

  const onSubmit = async (data: FormData) => {
    dispatch(clearError());
    const result = await dispatch(registerUser(data));
    if (registerUser.fulfilled.match(result)) {
      toast.success('Account created! Welcome 🎉');
    }
  };

  return (
    <div className="min-h-screen bg-[#060606] flex items-start sm:items-center justify-center p-4 py-10">
      <div className="fixed top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />

      <div className="w-full max-w-md relative">
        <div className="flex flex-col items-center mb-8">
          <div className="mb-4">
            {!logoFailed ? (
              <img src={LogoImage} alt={config.appName} className="w-14 h-14 object-contain" onError={() => setLogoFailed(true)} />
            ) : (
              <Ico name="chart" className="w-14 h-14 text-primary" />
            )}
          </div>
          <h1 className="font-display text-3xl font-bold text-white tracking-tight">{config.appName}</h1>
          <p className="text-gray-500 text-sm mt-1">Create your trading account</p>
        </div>

        <div className="bg-gray-900/80 backdrop-blur-md rounded-2xl border border-gray-800/60 p-8 shadow-2xl">
          {error && (
            <div className="mb-5">
              <Alert type="error" message={typeof error === 'string' ? error : 'Registration failed. Please check your inputs.'} />
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-3">
              <Input label="First Name" placeholder="John" error={errors.first_name?.message} {...register('first_name')} />
              <Input label="Last Name"  placeholder="Doe"  error={errors.last_name?.message}  {...register('last_name')} />
            </div>
            <Input
              label="Email" type="email" placeholder="you@example.com"
              prefix={<Ico name="mail" className="w-4 h-4" />}
              error={errors.email?.message}
              {...register('email')}
            />
            <Input
              label="Username" placeholder="johndoe"
              prefix={<span className="text-gray-500 text-sm">@</span>}
              error={errors.username?.message}
              {...register('username')}
            />
            <Input
              label="Password" type="password" placeholder="Min 8 characters"
              prefix={<Ico name="lock" className="w-4 h-4" />}
              error={errors.password?.message}
              {...register('password')}
            />
            <Input
              label="Confirm Password" type="password" placeholder="Repeat password"
              prefix={<Ico name="lock" className="w-4 h-4" />}
              error={errors.password_confirmation?.message}
              {...register('password_confirmation')}
            />
            <Button type="submit" fullWidth loading={loading} size="lg" className="mt-1">Create Account</Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-500 text-sm">
              Already have an account?{' '}
              <Link to="/login" className="text-primary hover:underline font-semibold">Sign in</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
